import '@machobear/design-system/reset'
import React from 'react'
import { ApolloProvider } from '@apollo/client'
import { Suspense, useEffect } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import isPropValid from '@emotion/is-prop-valid'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import styled, { StyleSheetManager } from 'styled-components'
import { QueryClientProvider, QueryErrorResetBoundary } from '@tanstack/react-query'
import { TourProvider } from '@reactour/tour'
import { ThemeProvider, ToastProvider, makeLinkComponent } from '@machobear/design-system'
import { lakshmi } from '@machobear/design-system/themes/lakshmi'
import { Link } from 'react-router-dom'
import Persona from 'persona'
import { themeProviderOptions } from '@/lib/themes'
import LoadingContainer from '@/components/loading'
import { ToastContainer } from '@/components/toast-notifications'
import client from '@/graphql-client'
import { colors } from '@/styles'
import media from '@/media'
import ErrorPage from '@/pages/Pages/ErrorPage'
import Viewport from '@/components/viewport'
import ScrollbarWidth from '@/components/scrollbar-width'
import { SSE } from '@/components/sse'
import queryClient from '@/components/query-client'
import TourPopover from '@/components/tour-popover'
import { disableScroll, enableScroll, updateTourState } from '@/utils/tour'
import handleSession from '@/utils/handle-session'
import useEventListener from '@/hooks/use-event-listener'
import useScrollToTop from '@/hooks/use-scroll-to-top'
import { SidebarProvider } from '@/hooks/use-sidebar-context'
import useMediaQuery from '@/hooks/use-media-query'
import UnauthenticatedApp from './unauthenticated-app'
import AuthenticatedApp from './authenticated-app'
import { useUserPilot } from './hooks/use-userpilot'
import useCurrentSession from './hooks/use-current-session'
import useZendeskWidget from './hooks/use-zendesk-widget'

const Wrapper = styled.div`
  display: flex;
  min-height: 100vh;
  flex-direction: column;

  ${media.lessThan('phoneMax')`
    min-height: 100%;
  `}
`

const Content = styled.div`
  flex: 1;
`

const onError = e => {
  if (e.message === 'ResizeObserver loop limit exceeded') {
    e.stopPropagation()
    e.stopImmediatePropagation()
  }
}

const Button = styled.button`
  display: flex;
  align-self: flex-end;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 4px;
  border: 1px solid ${colors.formAccent};
  background: ${colors.formAccent};
  box-shadow: 0px 1px 0px 0px rgba(0, 0, 0, 0.05);
  color: white;
  max-width: 70px;
`

const LinkComponent = makeLinkComponent(({ href, ...props }) => {
  // eslint-disable-next-line jsx-a11y/anchor-has-content
  return href.startsWith('/') ? <Link {...props} to={href} /> : <a {...props} href={href} />
})

const AppReady = ({ AppComponent, session }) => {
  useScrollToTop(true)
  const { i18n } = useTranslation()
  const lang = i18n.languages[0] || 'en'

  useUserPilot()
  useZendeskWidget()

  Persona.Client.preload()

  return (
    <Content>
      <Helmet titleTemplate="%s | liv.rent" htmlAttributes={{ lang }}>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Viewport />
      <ToastContainer />
      <QueryErrorResetBoundary>
        {({ reset }) => (
          <ErrorBoundary onReset={reset} FallbackComponent={ErrorPage}>
            <ThemeProvider
              linkComponent={LinkComponent}
              theme={lakshmi}
              options={themeProviderOptions}
            >
              <ToastProvider>
                <SSE />
                <AppComponent session={session} />
              </ToastProvider>
            </ThemeProvider>
          </ErrorBoundary>
        )}
      </QueryErrorResetBoundary>
      <ScrollbarWidth />
    </Content>
  )
}

const App = () => {
  const dispatch = useDispatch()
  const ready = useSelector(state => state.master.ready || false)
  const session = useCurrentSession()

  const isMobile = useMediaQuery('(max-width: 997px)')
  const isMediumScreen = useMediaQuery('(max-width: 1500px')
  const { t } = useTranslation()
  // For the positioning of the popover of the tour you may see for example [left - 375, top - 10]
  // When giving a position you can give an [x: number, y: number], and take in props { left, right, top, bottom, etc...}
  // The 375 and 10 are the position of the popover relative to the mask
  // For more info https://github.com/elrumordelaluz/reactour/tree/main/packages/tour#position-top--right--bottom--left--center--number-number
  const steps = [
    {
      selector: '.create-property-type',
      content: ({ setIsOpen }) => (
        <TourPopover
          title={t('l.sponsored_tour.create_listing.title', 'Start by creating a listing')}
          subtitle={t(
            'l.sponsored_tour.create_listing.text',
            'With a liv.rent listing, you can manage your sponsored listing and view our other features.'
          )}
          buttonTitle="Got it"
          setIsOpen={setIsOpen}
        />
      ),
      position: ({ left, bottom, top }) =>
        isMobile ? [left - 10, bottom + 20] : [left - 375, top - 10],
      padding: { mask: 10 },
      actionAfter: () => {
        updateTourState('propertyType')
      }
    },
    {
      selector: '.create-enter-details-content',
      content: ({ setIsOpen }) => (
        <TourPopover
          title={t('l.sponsored_tour.complete_listing.title', 'Complete your listing')}
          subtitle={t(
            'l.sponsored_tour.complete_listing.text',
            'With a liv.rent listing, you can manage your sponsored listing and view our other features.'
          )}
          buttonTitle="Got it"
          setIsOpen={setIsOpen}
        />
      ),
      position: ({ left, top }) => (!isMediumScreen ? [left - 375, top - 10] : 'top'),
      padding: { mask: 10, popover: isMediumScreen && [10, 20] },
      actionAfter: () => {
        updateTourState('listingDetails')
      }
    },
    {
      selector: '.create-sponsored',
      content: ({ setIsOpen }) => (
        <TourPopover
          title={t('l.sponsored_tour.increase_inquiries.title', 'Increase your inquiries')}
          subtitle={t(
            'l.sponsored_tour.increase_inquiries.text',
            'Select the duration of your Sponsored Listing and ensure you’ve checked the box next to ‘Sponsor your listing’. '
          )}
          buttonTitle="Got it"
          setIsOpen={setIsOpen}
        />
      ),
      position: isMediumScreen ? 'top' : 'left',
      padding: {
        mask: isMediumScreen ? [-5, 10] : 10,
        popover: isMediumScreen ? [-5, 20] : [20, 10]
      },
      actionAfter: () => {
        updateTourState('boostListing')
      }
    },
    {
      selector: '.listings-table-0',
      content: ({ setIsOpen }) => (
        <TourPopover
          title={t('l.sponsored_tour.select_listing.title', 'Select a listing')}
          subtitle={t(
            'l.sponsored_tour.select_listing.text',
            'Here, you can create a listing, view all your listings and select which listing you’d like to sponsor. '
          )}
          buttonTitle="Got it"
          setIsOpen={setIsOpen}
        />
      ),
      position: ({ left, top }) => [left - 10, top - 180],
      padding: { mask: 10 },
      actionAfter: () => {
        updateTourState('listingsTable')
      }
    },
    {
      selector: '.listings-sponsored',
      content: ({ setIsOpen }) => (
        <TourPopover
          title={t('l.sponsored_tour.manage_sponsored.title', 'Manage your sponsored listings')}
          subtitle={t(
            'l.sponsored_tour.manage_sponsored.text',
            'This is where you can manage and view details about your sponsored listings.'
          )}
          buttonTitle="Got it"
          setIsOpen={setIsOpen}
        />
      ),
      position: ({ left, top }) => [left - 375, top - 10],
      padding: { mask: 10, popover: [20, 0] },
      actionAfter: () => {
        updateTourState('listingDashboard')
      }
    },
    {
      selector: '.listings-unit-size',
      content: ({ setIsOpen }) => (
        <TourPopover
          title={t('l.sponsored_tour.complete_listing.title', 'Complete your listing')}
          subtitle={t(
            'l.sponsored_tour.complete_listing.text',
            'With a liv.rent listing, you can manage your sponsored listing and view our other features.'
          )}
          buttonTitle="Got it"
          setIsOpen={setIsOpen}
        />
      ),
      position: ({ left, top }) => [left - 10, top - 160],
      padding: { mask: 10 },
      actionAfter: () => {
        updateTourState('listingDetails')
      }
    },
    {
      selector: '.listings-table-mobile-0',
      content: ({ setIsOpen }) => (
        <TourPopover
          title={t('l.sponsored_tour.select_listing.title', 'Select a listing')}
          subtitle={t(
            'l.sponsored_tour.select_listing.text',
            'Here, you can create a listing, view all your listings and select which listing you’d like to sponsor. '
          )}
          buttonTitle="Got it"
          setIsOpen={setIsOpen}
        />
      ),
      position: 'bottom',
      padding: { mask: -10, popover: [-10, 0] },
      actionAfter: () => {
        updateTourState('listingsTable')
      }
    },
    {
      selector: '.listings-sponsored-mobile',
      content: ({ setIsOpen }) => (
        <TourPopover
          title={t('l.sponsored_tour.manage_sponsored.title', 'Manage your sponsored listings')}
          subtitle={t(
            'l.sponsored_tour.manage_sponsored.text',
            'This is where you can manage and view details about your sponsored listings.'
          )}
          buttonTitle="Got it"
          setIsOpen={setIsOpen}
        />
      ),
      position: ({ left, top }) => [left - 10, top + 80],
      padding: { mask: 10 },
      actionAfter: () => {
        updateTourState('listingDashboard')
      }
    },
    {
      selector: '.listings-create-publish',
      content: ({ setIsOpen }) => (
        <TourPopover
          title={t('l.sponsored_tour.publish_listing.title', 'Publish your listing')}
          subtitle={t(
            'l.sponsored_tour.publish_listing.text',
            'Once your sponsored listing is scheduled, only published and verified listings can go live.'
          )}
          buttonTitle="Got it"
          setIsOpen={setIsOpen}
        />
      ),
      position: ({ left, top }) => (isMobile ? 'top' : [left - 370, top - 100]),
      padding: { mask: 10, popover: isMobile ? [10, 20] : 10 },
      actionAfter: () => {
        updateTourState('publishListing')
      }
    },
    {
      selector: '.listings-table-empty',
      content: ({ setIsOpen }) => (
        <TourPopover
          title={t('l.sponsored_tour.select_listing.title', 'Select a listing')}
          subtitle={t(
            'l.sponsored_tour.select_listing.text',
            'Here, you can create a listing, view all your listings and select which listing you’d like to sponsor. '
          )}
          buttonTitle="Got it"
          setIsOpen={setIsOpen}
        />
      ),
      position: 'top',
      padding: { mask: -10, popover: [-10, 0] },
      actionAfter: () => {
        updateTourState('listingsTable')
      }
    },
    {
      selector: '.listings-table-draft',
      content: ({ setIsOpen }) => (
        <TourPopover
          title={t('l.sponsored_tour.select_listing.title', 'Select a listing')}
          subtitle={t(
            'l.sponsored_tour.select_listing.text',
            'Here, you can create a listing, view all your listings and select which listing you’d like to sponsor. '
          )}
          buttonTitle="Got it"
          setIsOpen={setIsOpen}
        />
      ),
      position: 'top',
      padding: { mask: -10, popover: [-10, 0] },
      actionAfter: () => {
        updateTourState('listingsTable')
      }
    },
    {
      selector: '.listings-table-empty-mobile',
      content: ({ setIsOpen }) => (
        <TourPopover
          title={t('l.sponsored_tour.select_listing.title', 'Select a listing')}
          subtitle={t(
            'l.sponsored_tour.select_listing.text',
            'Here, you can create a listing, view all your listings and select which listing you’d like to sponsor. '
          )}
          buttonTitle="Got it"
          setIsOpen={setIsOpen}
        />
      ),
      position: 'top',
      padding: { mask: -10, popover: [-10, 0] },
      actionAfter: () => {
        updateTourState('listingsTable')
      }
    }
  ]

  useEffect(() => {
    if (typeof window === 'undefined') return
    window.addEventListener('error', onError)
    return () => window.removeEventListener('error', onError)
  }, [])
  useEffect(() => {
    async function doLoad() {
      await handleSession(dispatch.master.load())
    }
    doLoad()
  }, [dispatch.master])

  useEventListener('storage', e => {
    if (e.key === 'session-id' && !!e.oldValue && e.oldValue !== e.newValue) {
      dispatch.session.clear()
    }
  })

  const AppComponent =
    !session || session?.registration_complete !== '1' ? UnauthenticatedApp : AuthenticatedApp

  return (
    <React.StrictMode>
      <Suspense fallback={null}>
        <ApolloProvider client={client}>
          <QueryClientProvider client={queryClient}>
            <StyleSheetManager shouldForwardProp={shouldForwardProp}>
              <HelmetProvider>
                <TourProvider
                  afterOpen={disableScroll}
                  beforeClose={enableScroll}
                  onClickMask={() => {}}
                  steps={steps}
                  showCloseButton={false}
                  showBadge={false}
                  showNavigation={false}
                  inViewThreshold={500}
                  disableKeyboardNavigation={true}
                  showDots={false}
                  components={{
                    Close: ({ onClick }) => <Button onClick={onClick}>Got it</Button>
                  }}
                  styles={{
                    popover: base => ({
                      ...base,
                      borderRadius: 8,
                      display: 'flex',
                      flexDirection: 'column-reverse',
                      padding: '0px',
                      gap: '16px',
                      transition: 'none',
                      background: 'transparent',
                      boxShadow: 'none'
                    }),
                    maskArea: base => ({ ...base, rx: 8 })
                  }}
                >
                  <SidebarProvider>
                    <Wrapper>
                      <LoadingContainer />
                      {ready && <AppReady AppComponent={AppComponent} session={session} />}
                    </Wrapper>
                  </SidebarProvider>
                </TourProvider>
              </HelmetProvider>
            </StyleSheetManager>
          </QueryClientProvider>
        </ApolloProvider>
      </Suspense>
    </React.StrictMode>
  )
}

export default App

function shouldForwardProp(propName, target) {
  if (typeof target === 'string') {
    return isPropValid(propName)
  }
  return true
}
